/**
 * Audit log + 인앱 알림 API 클라이언트.
 * 백엔드 /v0.1/audit-logs, /v0.1/notifications 와 1:1 매칭.
 */
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './api/apiClient';

export interface AuditLogItem {
  id: number;
  user_id: number;
  user_name: string;
  action: 'create' | 'update' | 'delete' | string;
  entity_type: string;
  entity_id: number;
  site_project_id?: number;
  summary: string;
  occurred_at: string;
}

export interface AuditLogListResponse {
  success: boolean;
  message?: string;
  data: AuditLogItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditLogFilter {
  entity_type?: string;
  entity_id?: number;
  user_id?: number;
  site_project_id?: number;
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface NotificationItem {
  id: number;
  actor_name: string;
  action: 'create' | 'update' | 'delete' | string;
  entity_type: string;
  entity_id: number;
  site_project_id?: number;
  summary: string;
  occurred_at: string;
  read_at?: string | null;
}

export interface NotificationListResponse {
  success: boolean;
  message?: string;
  data: NotificationItem[];
  total: number;
  unread_count: number;
  limit: number;
  offset: number;
}

export interface UnreadCountResponse {
  success: boolean;
  unread_count: number;
  message?: string;
}

export interface ActionResponse {
  success: boolean;
  message?: string;
}

export class AuditService {
  /** 변경 이력 조회 — entity_type/entity_id/site_project_id 등으로 필터. */
  static async listAuditLogs(filter: AuditLogFilter = {}): Promise<AuditLogListResponse> {
    const params: Record<string, string> = {};
    Object.entries(filter).forEach(([k, v]) => {
      if (v != null && v !== '') params[k] = String(v);
    });
    const resp = await apiClient.get<AuditLogListResponse>(API_ENDPOINTS.AUDIT.LOGS, { params });
    return resp.data;
  }

  /** 본인 알림 목록 조회. unread=true 면 미열람만. */
  static async listNotifications(opts: { unread?: boolean; limit?: number; offset?: number } = {}): Promise<NotificationListResponse> {
    const params: Record<string, string> = {};
    if (opts.unread) params.unread = 'true';
    if (opts.limit != null) params.limit = String(opts.limit);
    if (opts.offset != null) params.offset = String(opts.offset);
    const resp = await apiClient.get<NotificationListResponse>(API_ENDPOINTS.AUDIT.NOTIFICATIONS, { params });
    return resp.data;
  }

  /** 미열람 알림 수 — 종 배지 폴링 전용. */
  static async unreadCount(): Promise<number> {
    const resp = await apiClient.get<UnreadCountResponse>(API_ENDPOINTS.AUDIT.NOTIFICATIONS_UNREAD_COUNT);
    return resp.data.success ? resp.data.unread_count : 0;
  }

  /** 단건 읽음 처리. */
  static async markRead(id: number): Promise<void> {
    await apiClient.post<ActionResponse>(API_ENDPOINTS.AUDIT.NOTIFICATION_READ(id));
  }

  /** 전체 읽음 처리. */
  static async markAllRead(): Promise<void> {
    await apiClient.post<ActionResponse>(API_ENDPOINTS.AUDIT.NOTIFICATIONS_READ_ALL);
  }
}
